/*
 * Home — the front page of the publication (Handoff v3 §1).
 * Flagship hero featuring Porsha, links to ALL topic pages including
 * Date Night, featured stories across sections, newsletter conversion.
 */
import { Link } from "wouter";
import SiteLayout from "@/components/SiteLayout";
import StoryCard from "@/components/StoryCard";
import NewsletterCTA from "@/components/NewsletterCTA";
import Seo from "@/components/Seo";
import ResponsiveImage from "@/components/ResponsiveImage";
import { sections } from "@/content/sections";
import { featuredArticles, articles } from "@/content/articles";

export default function Home() {
  const latest = [...articles]
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 6);

  return (
    <SiteLayout>
      <Seo
        title="According to Porsha — Real. Raw. Relevant."
        description="A premium editorial publication: The Tea, Fashion, Travel, Haute to Watch, Detroit Guide, Things to Do, Current Events, Date Night, and The Monthly Edit."
      />

      {/* Flagship hero — Porsha portrait + editorial statement */}
      <section className="relative overflow-hidden">
        <div className="container">
          <div className="grid gap-10 lg:grid-cols-12 items-center py-14 lg:py-20">
            <div className="lg:col-span-6 relative z-10">
              <p className="kicker reveal">A Premium Editorial Publication</p>
              <h1
                className="font-display reveal mt-4 font-black leading-[1.02]"
                style={{ fontSize: "clamp(2.8rem, 6.5vw, 5rem)", animationDelay: "80ms" }}
              >
                According to{" "}
                <span className="font-script font-normal" style={{ color: "var(--violet)" }}>
                  Porsha
                </span>
              </h1>
              <p
                className="reveal mt-5 text-lg md:text-xl text-foreground/85 leading-relaxed max-w-lg"
                style={{ animationDelay: "160ms" }}
              >
                Culture, style, city life, and the conversations that matter —
                told with a voice that doesn't flinch.
              </p>
              <p
                className="reveal kicker mt-6"
                style={{ color: "var(--violet)", animationDelay: "240ms" }}
              >
                Real. Raw. Relevant.
              </p>
              <div className="reveal mt-9 flex flex-wrap gap-4" style={{ animationDelay: "320ms" }}>
                <Link
                  href="/the-tea"
                  className="border border-[var(--gold)] bg-[var(--gold)] px-7 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-[var(--ink)] hover:bg-[var(--gold-2)] transition-all active:scale-[0.97]"
                >
                  Start with The Tea
                </Link>
                <Link
                  href="/subscribe"
                  className="border border-border px-7 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-foreground/85 hover:border-[var(--gold)] hover:text-[var(--gold-2)] transition-all active:scale-[0.97]"
                >
                  The Monthly Edit
                </Link>
              </div>
            </div>
            <div className="lg:col-span-6 relative">
              <div
                className="pointer-events-none absolute -inset-8 opacity-25"
                style={{
                  background:
                    "radial-gradient(ellipse at 60% 40%, var(--gold) 0%, transparent 60%)",
                }}
              />
              <div className="editorial-img relative overflow-hidden border border-[var(--gold)]/30">
                <ResponsiveImage
                  src="/manus-storage/porsha-front-page_a1968843.png"
                  alt="Porsha — in a black blazer before the Eiffel Tower at golden hour"
                  priority
                  sizes="(min-width: 1024px) 50vw, 100vw"
                  className="ken-burns w-full aspect-[3/4] max-h-[640px] object-cover"
                />
              </div>
              <p className="mt-3 text-[0.65rem] uppercase tracking-[0.24em] text-muted-foreground text-right">
                The voice behind the microphone
              </p>
            </div>
          </div>
        </div>
        <div className="gold-rule-center opacity-70" />
      </section>

      {/* Section index — asymmetric editorial spread, every topic incl. Date Night */}
      <section className="container mt-16">
        <div className="flex items-baseline gap-4 mb-10">
          <h2 className="kicker">The Sections</h2>
          <div className="gold-rule flex-1 opacity-60" />
          <p className="hidden md:block text-xs text-muted-foreground uppercase tracking-[0.18em]">
            Eight topics. One voice.
          </p>
        </div>
        <div className="grid gap-x-8 gap-y-12 grid-cols-2 lg:grid-cols-12">
          {sections.map((s, i) => {
            // Editorial rhythm: lead item spans wide, others offset in mixed sizes
            const span =
              i === 0
                ? "col-span-2 lg:col-span-5"
                : i % 4 === 1
                  ? "lg:col-span-4"
                  : i % 4 === 2
                    ? "lg:col-span-3"
                    : i % 4 === 3
                      ? "lg:col-span-3 lg:mt-10"
                      : "lg:col-span-4 lg:mt-4";
            const ratio = i === 0 ? "aspect-[4/3]" : i % 3 === 0 ? "aspect-[3/4]" : "aspect-[4/5]";
            return (
              <Link
                key={s.slug}
                href={`/${s.slug}`}
                className={`story-card group block reveal ${span}`}
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="editorial-img overflow-hidden bg-secondary">
                  <ResponsiveImage
                    src={s.heroImage}
                    alt={s.heroImageAlt}
                    sizes="(min-width: 1024px) 33vw, 50vw"
                    className={`w-full object-cover ${ratio}`}
                  />
                </div>
                <div className="pt-4">
                  <p className="text-[0.6rem] uppercase tracking-[0.26em] text-muted-foreground">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3
                    className={`font-display mt-1 font-bold group-hover:text-[var(--gold-2)] transition-colors ${
                      i === 0 ? "text-3xl" : "text-2xl"
                    }`}
                  >
                    {s.name}
                  </h3>
                  <p className="font-script mt-1 text-xl" style={{ color: "var(--violet)" }}>
                    {s.tagline}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Featured across the publication — asymmetric lead + offset rail */}
      <section className="container mt-20">
        <div className="flex items-baseline gap-4 mb-10">
          <h2 className="kicker">Featured This Week</h2>
          <div className="gold-rule flex-1 opacity-60" />
        </div>
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            {featuredArticles[0] && <StoryCard article={featuredArticles[0]} large />}
          </div>
          <div className="lg:col-span-4 lg:col-start-9 grid gap-10 content-start lg:mt-14">
            {featuredArticles.slice(1, 3).map((a, i) => (
              <StoryCard key={a.slug} article={a} index={i + 1} />
            ))}
          </div>
        </div>
      </section>

      {/* Latest across all sections — staggered editorial grid */}
      <section className="container mt-20">
        <div className="flex items-baseline gap-4 mb-10">
          <h2 className="kicker">The Latest</h2>
          <div className="gold-rule flex-1 opacity-60" />
        </div>
        <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3 [&>*:nth-child(3n+2)]:lg:mt-12">
          {latest.map((a, i) => (
            <StoryCard key={a.slug} article={a} index={i} />
          ))}
        </div>
      </section>

      {/* Newsletter conversion */}
      <div className="container mt-20">
        <NewsletterCTA location="home_page" />
      </div>
    </SiteLayout>
  );
}
